import router from '@/router';
import { NavigationGuard } from 'vue-router';

/**
 * Some pages may not *require* authentication, but if the user happens to be logged in then the
 * authenticated version of the page should be preferred and redirected to. This `NavigationGuard`
 * function handles that scenario. One such example is /public-claims-form -> /claims2.
 *
 * A route can declare this intention by setting the `meta.preferAuthedLocation` property. For
 * example:
 *
 * ```json
 * {
 *     // ...
 *     "meta": {
 *         "preferAuthedLocation": "/path/to/authed/page"
 *     }
 *     // ...
 * }
 * ```
 *
 * Note that `meta.preferAuthedLocation` in the example above is a simple string path, but anything
 * that satisfies type `RawLocation` can be specified. In fact, that is what `Router.replace()`
 * expects as its sole parameter and so `meta.preferAuthedLocation` is passed directly.
 *
 * Also note that this `NavigationGuard` must be registered before the generic authentication
 * checker below, because when a logged in user attempts to navigate to a page that has
 * `meta.auth === false`, it will push that person to their home page instead.
 */
const preferAuthedLocationGuard: NavigationGuard = (to, _from, next): void => {
     if (to.meta?.preferAuthedLocation && window.isLoggedIn) {
          router.replace(to.meta.preferAuthedLocation);
     } else {
          next();
     }
};

router.beforeEach(preferAuthedLocationGuard);

router.beforeEach(async (to, from, next) => {
     // Redirect to the login page if not authenticated
     if (to.meta?.auth !== undefined) {
          if (to.meta.auth) {
               if (window.isLoggedIn) {
                    next();
               } else {
                    window.localStorage.setItem('previousUrl', window.location.href);
                    router.push({ name: 'login' });

               }
          } else {
               if (window.isLoggedIn) {
                    router.push({
                         name: 'home',
                    });
               } else {
                    next();
               }
          }
     } else {
          next();
     }
});
